import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Badge,
  ButtonText,
  ButtonIcon
} from '@morressier/alembic';
import { Box, Text, Flex, Tooltip } from '@chakra-ui/react';
import { IPeerReviewListItem } from '@publishing-engine/domain/views';
import {
  recommendationBadges,
  reviewStatusBadges
} from 'src/components/Review/Badges';
import {
  formatDateWithoutTimeUTC,
  formatDeadlineDate,
  formatToUTCDate,
  timeFormatDeadlineDate
} from 'src/utils/date';
import { RATING_OPTIONS } from 'src/utils/reviews';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { faBars } from '@fortawesome/pro-solid-svg-icons';
import { calculateDaysToDeadline } from 'src/utils/deadlines';
import {
  IPeerReviewWithdrawActions,
  IReviewer
} from '@publishing-engine/domain/models';
import { URGENCY_THRESHOLD } from '@publishing-engine/domain/fixtures/PeerReview';
import { useSubmissionData } from '../../../contexts/SubmissionDataProvider';
import { useSubmissionReviewsData } from 'src/contexts/SubmissionReviewsDataProvider';
import ProtectedReviewWorkflowAction from 'src/components/ProtectedAction/ProtectedReviewWorkflowAction';
import { AdHocModalOpen } from '../AuthorsTab/ComposeEmailModal';
import { ReviewedValidstate } from 'src/constants';

export const renderReviewStatusColumn = (
  review: IPeerReviewListItem,
  onDeclineReviewReason?: (reviewId: string) => void
) => {
  const deadlineExtensionRequested =
    review?.workflow?.deadlineExtensions?.status === 'requested';
  const currentState = deadlineExtensionRequested
    ? 'extensionRequested'
    : review?.workflow?.currentState;

  const declinedReviewReason =
    review?.caseData?.additionalInformation?.declineReason;
  const recommendedReviewers =
    review?.caseData?.additionalInformation?.recommendedReviewers;

  const hasAdditionalReviewInfo =
    declinedReviewReason ||
    (recommendedReviewers && recommendedReviewers.length > 0);

  const currentStateIncludingDeclinedWithReason =
    declinedReviewReason && currentState === 'declined'
      ? 'declinedWithReason'
      : currentState;

  const statusBadge = currentState
    ? reviewStatusBadges[currentStateIncludingDeclinedWithReason]
    : reviewStatusBadges.invited;

  if (currentState === 'declined' && hasAdditionalReviewInfo) {
    return (
      <Box width="100%" overflow="hidden">
        <Flex alignItems="center" gap={2}>
          <Box minWidth={0} overflow="hidden">
            {statusBadge}
          </Box>

          <Tooltip
            shouldWrapChildren
            hasArrow
            label="This Reviewer has sent information about this decision."
            placement="top"
          >
            <Box
              cursor="pointer"
              onClick={e => {
                e.stopPropagation();
                onDeclineReviewReason?.(review.id);
              }}
              flexShrink={0}
            >
              <Badge size="context" variant="caution" alignItems="center">
                <FontAwesomeIcon icon={faInfoCircle} />
              </Badge>
            </Box>
          </Tooltip>
        </Flex>
      </Box>
    );
  }

  return (
    <Box width="100%" overflow="hidden">
      {statusBadge}
    </Box>
  );
};

const REVIEW_INVITE_ACTIONS = [
  'ReviewRequested#manual',
  'ReviewRequested#automated',
  'ReviewRequested#manualBoardMember',
  'ReviewRequested#automaticBoardMember',
  'ReviewInRevisionRequested#manual',
  'ReviewInRevisionRequested#automated',
  'ReviewInRevisionRequested#manualBoardMember',
  'ReviewInRevisionRequested#automaticBoardMember',
  'RevisionReviewRequested',
  'DeclinedReviewInvitationResent',
  'ExpiredReviewInvitationResent',
  'WithdrawnReviewInvitationResent#invited',
  'WithdrawnReviewInvitationResent#assessment'
];

const getInvitedDate = (review: IPeerReviewListItem): string | undefined =>
  review.timeline?.invitedDate ??
  review.workflow.states?.find(
    state =>
      state.completedWith?.action &&
      REVIEW_INVITE_ACTIONS.includes(state.completedWith.action)
  )?.completedWith?.date;

export const renderDeadlineColumn = (review: IPeerReviewListItem) => {
  const { currentState } = review.workflow;
  const invitedDate = getInvitedDate(review);

  const deadlineState =
    currentState === 'revisionInvited' || currentState === 'revisionAssessment'
      ? currentState
      : review.workflow.previousState;
  let isUrgent: boolean | null = null;
  let deadlineDate: Date | null = null;
  let deadlineText = '';

  if (review.workflow.currentDeadline) {
    deadlineDate = new Date(review.workflow.currentDeadline);
    const daysToDeadline = deadlineDate
      ? calculateDaysToDeadline(deadlineDate)
      : null;

    isUrgent =
      typeof daysToDeadline === 'number' && daysToDeadline <= URGENCY_THRESHOLD;

    if (daysToDeadline && daysToDeadline >= 0) {
      if (deadlineState === 'revisionInvited') {
        deadlineText = `${daysToDeadline} days to accept the invitation`;
      } else if (deadlineState === 'revisionAssessment') {
        deadlineText = `${daysToDeadline} days to submit their Review`;
      }
    } else {
      deadlineText = daysToDeadline
        ? `${Math.abs(daysToDeadline)} days overdue`
        : typeof daysToDeadline === 'number' && daysToDeadline === 0
        ? 'Today is the deadline'
        : '';
    }
  }

  if (!deadlineText) {
    deadlineText = deadlineDate
      ? `Deadline: ${formatDeadlineDate(deadlineDate)}`
      : invitedDate
      ? `Invitation sent: ${formatToUTCDate(new Date(invitedDate))}`
      : 'Invitation and deadline information is not available';
  }

  return (
    <Flex
      direction="column"
      gap={1}
      textStyle="textContext"
      color={isUrgent ? 'actionSecondaryCautionContent' : ''}
    >
      <Flex gap={1} alignItems="center">
        <Text textStyle="textContext" fontWeight="bold" fontSize={11}>
          On:
        </Text>
        <Text textStyle="textBody" fontSize={11}>
          {invitedDate
            ? formatDateWithoutTimeUTC(new Date(invitedDate))
            : 'N/A'}
        </Text>

        {invitedDate && (
          <Tooltip
            label={`Invitation sent On: ${timeFormatDeadlineDate(
              new Date(invitedDate)
            )}`}
            aria-label="Invitation Info"
            placement="top"
            hasArrow
            shouldWrapChildren
          >
            <ButtonIcon
              size="small"
              ariaLabel="Invitation Info Icon"
              icon={<FontAwesomeIcon icon={faInfoCircle} />}
            />
          </Tooltip>
        )}
      </Flex>

      <Flex gap={1} alignItems="center">
        <Text textStyle="textContext" fontWeight="bold" fontSize={11}>
          Due:
        </Text>
        <Text textStyle="textBody" fontSize={11}>
          {deadlineDate ? `${formatDateWithoutTimeUTC(deadlineDate)}` : 'N/A'}
        </Text>

        {deadlineDate && (
          <Tooltip
            label={
              <Text>
                {deadlineText}
                <br /> Due: {timeFormatDeadlineDate(deadlineDate)}
              </Text>
            }
            aria-label="Deadline Info"
            placement="top"
            hasArrow
            shouldWrapChildren
          >
            <ButtonIcon
              size="small"
              ariaLabel="Deadline Info Icon"
              icon={<FontAwesomeIcon icon={faInfoCircle} />}
            />
          </Tooltip>
        )}
      </Flex>
    </Flex>
  );
};

export const renderRecommendationColumn = (review: IPeerReviewListItem) => {
  const { hasSubmittedReviewInCurrentRound } = useSubmissionReviewsData();
  const { availableReviewActions } = useSubmissionData();

  const isAwaitingReview =
    availableReviewActions.contains(review, 'RequestRevisionReview') ||
    review.workflow.currentState === 'revisionAssessment';

  const isReviewerWidthdrawnBeforeReinvite =
    review.workflow.currentState === 'withdrawn';

  const isAwaitingReinvitation =
    review.workflow.currentState === 'awaitingRevisionReviewRequest';

  const isReviewDeclined = availableReviewActions.contains(
    review,
    'ResendDeclinedReviewInvitation'
  );

  const isReviewExpired = availableReviewActions.contains(
    review,
    'ResendExpiredReviewInvitation'
  );

  const isReviewWithdrawn = availableReviewActions.contains(
    review,
    `ResendWithdrawnReviewInvitation#${review.subtype}`
  );

  const isRevisionInvited = availableReviewActions.contains(
    review,
    'EditorialWithdrawRevisionInvitedReviewInvitation'
  );

  if (
    isReviewWithdrawn ||
    isReviewDeclined ||
    isReviewExpired ||
    isRevisionInvited ||
    isReviewerWidthdrawnBeforeReinvite ||
    isAwaitingReinvitation
  ) {
    return 'N/A';
  }

  if (isAwaitingReview || !hasSubmittedReviewInCurrentRound(review.id)) {
    return recommendationBadges.awaitingReview;
  }

  if (!review.caseData.revisionAssessment?.recommendation) {
    return 'N/A';
  }

  return recommendationBadges[
    review.caseData.revisionAssessment.recommendation
  ];
};

const renderScoreBadge = (scoreKey: string, justification: any) => {
  const score = RATING_OPTIONS[scoreKey];

  return score ? (
    <Badge variant="neutral" size="context" isTruncated>
      <Tooltip label={`${justification || 'N/A'}`} placement="bottom" hasArrow>
        <Box as="span" display="inline-block">
          {`${score.value} (${score.label})`}
        </Box>
      </Tooltip>
    </Badge>
  ) : (
    <Badge variant="neutral" size="context" isTruncated>
      Awaiting
    </Badge>
  );
};

export const renderReviewScoreColumn = (review: IPeerReviewListItem) => {
  // The review score is only stored in the first assessment (review.caseData.firstAssessment), which means the editor can provide a rating to the reviewer only once, regardless of the revision. So, we can safely access it here.

  const { reviewScore } = review.caseData.firstAssessment || {};

  if (reviewScore?.score) {
    return renderScoreBadge(reviewScore.score, reviewScore.justification);
  }

  switch (review.workflow.currentState) {
    case 'invited':
    case 'declined':
    case 'expired':
    case 'withdrawn':
    case 'awaitingRevisionReviewRequest':
      return 'N/A';

    case 'rescinded':
    case 'firstAssessment':
    case 'revisionInvited':
    case 'revisionAssessment':
      return recommendationBadges.awaitingReview;

    default:
      return (
        <Badge variant="neutral" size="context">
          Awaiting
        </Badge>
      );
  }
};

const renderMenuItem = (
  label: string,
  onClick: () => void,
  textColor?: string,
  disabled?: boolean
) => {
  return (
    <Box onClick={event => event.stopPropagation()}>
      {disabled ? (
        <>
          <MenuItem disabled={disabled} ariaLabel={label} onClick={onClick}>
            <Text textColor={textColor}>{label}</Text>
          </MenuItem>
        </>
      ) : (
        <MenuItem ariaLabel={label} onClick={onClick}>
          <Text textColor={textColor}>{label}</Text>
        </MenuItem>
      )}
    </Box>
  );
};

export const renderActionsColumn = (
  review: IPeerReviewListItem,
  onSelectReviewId: (reviewId: string) => void,
  onSelectedAuthor: (author: IReviewer | null) => void,
  onGrantExtension: (reviewId: string) => void,
  onRateReview: (reviewId: string) => void,
  onReinviteReviewer: (reviewId: string) => void,
  onDeclineReviewReason: (reviewId: string) => void,
  onResendInvite: (reviewId: string) => void,
  onWithdrawReviewer: (
    reviewId: string,
    reviewer: IReviewer,
    actionName: IPeerReviewWithdrawActions
  ) => void,
  onSendEmail: (modal: AdHocModalOpen) => void,
  onRescindReview: (reviewId: string) => void,
  isUserDeactivated?: boolean,
  onReassignBoardMember?: (reviewId: string) => void
) => {
  const deadlineExtensionRequested =
    review?.workflow?.deadlineExtensions?.status === 'requested';
  const { availableReviewActions, isTaskCompleter } = useSubmissionData();
  const { currentState } = review.workflow;
  const canExtendDeadline =
    currentState === 'rescinded' ||
    availableReviewActions.contains(review, 'ExtendReviewDeadline');

  const renderItem = (
    label: string,
    onClick: () => void,
    textColor?: string,
    isDisabled?: boolean
  ) => renderMenuItem(label, onClick, textColor, isDisabled);

  const subtype =
    review?.workflow.previousState === 'invited' ||
    review?.workflow.previousState === 'revisionInvited'
      ? 'invited'
      : 'assessment';
  const renderWithdrawReviewerMenuItem = (
    actionName: IPeerReviewWithdrawActions,
    isDisabled?: boolean
  ) => {
    if (currentState === 'awaitingRevisionReviewRequest') return null;

    return availableReviewActions.contains(review, actionName) ? (
      <ProtectedReviewWorkflowAction
        reviewId={review.id}
        actionName={actionName}
      >
        {renderMenuItem(
          'Withdraw Reviewer',
          () => {
            onWithdrawReviewer(review.id, review.reviewer, actionName);
          },
          'actionSecondaryCautionContent',
          isDisabled
        )}
      </ProtectedReviewWorkflowAction>
    ) : null;
  };

  const renderDeclineExpiredReinviteMenuItems = (isDisabled?: boolean) => {
    if (review.isBoardMember) return null;

    // Rescinded reviewers rework via SubmitRevisionAssessment — same as first-round
    // peer review (no "Resend Invitation" on rescinded).
    if (currentState === 'rescinded') return null;

    const isRequestRevisionReview = availableReviewActions.contains(
      review,
      'RequestRevisionReview'
    );
    const isResendExpiredReviewInvitation = availableReviewActions.contains(
      review,
      'ResendExpiredReviewInvitation'
    );
    const isResendDeclinedReviewInvitation = availableReviewActions.contains(
      review,
      'ResendDeclinedReviewInvitation'
    );

    const actionName = isRequestRevisionReview
      ? 'RequestRevisionReview'
      : isResendExpiredReviewInvitation
      ? 'ResendExpiredReviewInvitation'
      : isResendDeclinedReviewInvitation
      ? 'ResendDeclinedReviewInvitation'
      : null;

    if (!actionName) return null;

    return (
      <ProtectedReviewWorkflowAction
        reviewId={review.id}
        actionName={actionName}
      >
        {renderMenuItem(
          'Resend Invitation',
          () => onResendInvite(review.id),
          undefined,
          isDisabled
        )}
      </ProtectedReviewWorkflowAction>
    );
  };

  return (
    <Menu ariaLabel="Actions">
      <MenuButton>
        <ButtonText>
          <Flex gap={2} alignItems="center">
            <FontAwesomeIcon size="lg" icon={faBars} />
          </Flex>
        </ButtonText>
      </MenuButton>
      <MenuList>
        {ReviewedValidstate.includes(currentState) &&
          renderItem('View Review', () => {
            onSelectReviewId(review.id);
          })}

        {renderItem(
          'View Reviewer Profile',
          () => onSelectedAuthor(review.reviewer),
          undefined,
          isUserDeactivated ?? false
        )}

        {isTaskCompleter &&
          renderItem(
            'Send Email',
            () =>
              onSendEmail({
                status: true,
                defaultEmail: [review.reviewer.email]
              }),
            undefined,
            isUserDeactivated ?? false
          )}

        {review.caseData?.additionalInformation?.declineReason &&
          renderItem(
            'View Additional Info',
            () => onDeclineReviewReason(review.id),
            undefined,
            isUserDeactivated ?? false
          )}
        {review.canRateReview &&
          renderMenuItem(
            'Rate Review',
            () => onRateReview(review.id),
            '',
            isUserDeactivated ?? false
          )}

        {availableReviewActions.contains(review, 'RescindFirstAssessment') &&
          renderItem(
            'Rescind Review',
            () => onRescindReview(review.id),
            undefined,
            isUserDeactivated ?? false
          )}

        {!review.isBoardMember &&
          availableReviewActions.contains(
            review,
            `ResendWithdrawnReviewInvitation#${subtype}`
          ) && (
            <ProtectedReviewWorkflowAction
              reviewId={review.id}
              actionName={`ResendWithdrawnReviewInvitation#${subtype}`}
            >
              {renderItem(
                'Resend Invitation',
                () => {
                  onResendInvite(review.id);
                },
                undefined,
                isUserDeactivated ?? false
              )}
            </ProtectedReviewWorkflowAction>
          )}

        {review.isBoardMember &&
          onReassignBoardMember &&
          [
            'declined',
            'expired',
            'withdrawn',
            'awaitingRevisionReviewRequest'
          ].includes(currentState) && (
            <Box sx={{ '& > span': { display: 'block', width: '100%' } }}>
              {renderItem(
                'Reassign Reviewer',
                () => onReassignBoardMember(review.id),
                undefined,
                isUserDeactivated ?? false
              )}
            </Box>
          )}

        {renderDeclineExpiredReinviteMenuItems(isUserDeactivated ?? false)}

        {renderWithdrawReviewerMenuItem(
          'EditorialWithdrawAwaitingRevisionReviewInvitation',
          isUserDeactivated ?? false
        )}
        {renderWithdrawReviewerMenuItem(
          'EditorialWithdrawRevisionAssessmentReviewInvitation',
          isUserDeactivated ?? false
        )}
        {renderWithdrawReviewerMenuItem(
          'EditorialWithdrawRevisionInvitedReviewInvitation',
          isUserDeactivated ?? false
        )}
        {deadlineExtensionRequested
          ? renderItem(
              'Review Extension Request',
              () => {
                onGrantExtension(review.id);
              },
              undefined,
              isUserDeactivated ?? false
            )
          : canExtendDeadline &&
            renderItem(
              'Extend Deadline',
              () => {
                onGrantExtension(review.id);
              },
              undefined,
              isUserDeactivated ?? false
            )}
      </MenuList>
    </Menu>
  );
};
